import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "../ui/card";
import { BehaviorEntry, BehaviorCorrelation } from "../../types/tradingJournal";
import {
  TrendingUp,
  BarChart2,
  Info,
  ChevronRight,
  Calendar,
  Layers,
  Filter,
  ArrowDownRight,
  ArrowUpRight,
} from "lucide-react";
import BehaviorInsightsChart from "./BehaviorInsightsChart";
import BehaviorCorrelationTable from "./BehaviorCorrelationTable";
import { Button } from "../ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Badge } from "../ui/badge";

interface BehaviorChartsProps {
  filteredBehaviorEntries: BehaviorEntry[];
  behaviorCorrelations: BehaviorCorrelation[];
}

const BehaviorCharts = ({
  filteredBehaviorEntries,
  behaviorCorrelations,
}: BehaviorChartsProps) => {
  const [displayMode, setDisplayMode] = useState<"chart" | "table">("chart");
  const [correlationView, setCorrelationView] = useState<
    "all" | "positive" | "negative"
  >("all");

  // Prepare data for the line chart (Daily P&L Trend)
  const lineChartData = filteredBehaviorEntries.map((entry) => ({
    date: entry.date,
    pnl: entry.dailyPnL,
    tradeCount: entry.numberOfTrades,
  }));

  // Prepare and filter behavior correlation data based on view mode
  const getCorrelationBarData = () => {
    if (behaviorCorrelations.length === 0) return [];

    const filteredCorrelations = [...behaviorCorrelations]
      .filter((corr) => {
        if (correlationView === "positive") return corr.avgPnL > 0;
        if (correlationView === "negative") return corr.avgPnL < 0;
        return true;
      })
      .sort((a, b) => Math.abs(b.avgPnL) - Math.abs(a.avgPnL))
      .slice(0, 7); // Limit to top 7 for readability

    return filteredCorrelations.map((corr) => ({
      name: `${corr.behavior}: ${corr.value}`,
      value: Number(corr.avgPnL.toFixed(2)),
      tradeCount: Math.round(
        (corr.occurrenceRate * filteredBehaviorEntries.length) / 100
      ),
    }));
  };

  // Additional insights calculation
  const getRecentTrend = () => {
    if (filteredBehaviorEntries.length < 5) return null;

    const sortedEntries = [...filteredBehaviorEntries].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    const recentEntries = sortedEntries.slice(-3);
    const olderEntries = sortedEntries.slice(0, -3);

    if (recentEntries.length < 2 || olderEntries.length < 2) return null;

    const recentAvg =
      recentEntries.reduce((sum, entry) => sum + entry.dailyPnL, 0) /
      recentEntries.length;
    const olderAvg =
      olderEntries.reduce((sum, entry) => sum + entry.dailyPnL, 0) /
      olderEntries.length;

    const percentChange = ((recentAvg - olderAvg) / Math.abs(olderAvg)) * 100;

    return {
      recentAvg,
      percentChange,
      improved: recentAvg > olderAvg,
    };
  };

  const recentTrend = getRecentTrend();
  const barChartData = getCorrelationBarData();

  console.log("Line chart data:", lineChartData);
  console.log("Bar chart data:", barChartData);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
      <Card className="chart-card overflow-hidden">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-market-teal" />
            <CardTitle className="chart-title">Daily P&L Trend</CardTitle>
          </div>
          <CardDescription className="chart-subtitle">
            Your profit/loss performance over time
          </CardDescription>
        </CardHeader>
        <CardContent>
          <BehaviorInsightsChart data={lineChartData} type="line" />
        </CardContent>
        {recentTrend && (
          <CardFooter className="pt-0 pb-4 px-6">
            <div
              className={`flex items-center p-2 rounded-md ${
                recentTrend.improved ? "bg-green-500/10" : "bg-red-500/10"
              }`}
            >
              {recentTrend.improved ? (
                <ArrowUpRight className="h-4 w-4 text-green-400 mr-2" />
              ) : (
                <ArrowDownRight className="h-4 w-4 text-red-400 mr-2" />
              )}
              <span className="text-sm">
                Recent trend:
                <span
                  className={
                    recentTrend.improved ? " text-green-400" : " text-red-400"
                  }
                >
                  {" "}
                  {recentTrend.improved ? "+" : ""}
                  {recentTrend.percentChange.toFixed(1)}%
                </span>
              </span>
            </div>
          </CardFooter>
        )}
      </Card>

      <Card className="chart-card overflow-hidden">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BarChart2 className="h-5 w-5 text-market-teal" />
              <CardTitle className="chart-title">
                Behavior Impact Analysis
              </CardTitle>
            </div>
            <div className="flex items-center">
              <Tabs
                value={correlationView}
                onValueChange={(value) =>
                  setCorrelationView(value as "all" | "positive" | "negative")
                }
                className="w-auto"
              >
                <TabsList className="h-8 bg-white/5">
                  <TabsTrigger
                    value="all"
                    className="text-xs px-2 py-1 h-6 data-[state=active]:bg-white/10"
                  >
                    All
                  </TabsTrigger>
                  <TabsTrigger
                    value="positive"
                    className="text-xs px-2 py-1 h-6 data-[state=active]:bg-green-500/20 data-[state=active]:text-green-400"
                  >
                    Positive
                  </TabsTrigger>
                  <TabsTrigger
                    value="negative"
                    className="text-xs px-2 py-1 h-6 data-[state=active]:bg-red-500/20 data-[state=active]:text-red-400"
                  >
                    Negative
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 ml-1"
                onClick={() =>
                  setDisplayMode(displayMode === "chart" ? "table" : "chart")
                }
              >
                {displayMode === "chart" ? (
                  <Layers className="h-4 w-4" />
                ) : (
                  <BarChart2 className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
          <CardDescription className="chart-subtitle">
            How your behaviors correlate with trading results
          </CardDescription>
        </CardHeader>
        <CardContent>
          {behaviorCorrelations.length > 0 ? (
            displayMode === "chart" ? (
              <BehaviorInsightsChart data={barChartData} type="bar" />
            ) : (
              <BehaviorCorrelationTable
                correlations={behaviorCorrelations.filter((corr) => {
                  if (correlationView === "positive") return corr.avgPnL > 0;
                  if (correlationView === "negative") return corr.avgPnL < 0;
                  return true;
                })}
              />
            )
          ) : (
            <div className="flex flex-col items-center justify-center h-[300px] text-center p-4">
              <Info className="h-10 w-10 text-white/20 mb-3" />
              <p className="text-white/60 mb-1">
                Not enough data for behavior analysis
              </p>
              <p className="text-white/40 text-sm">
                Add at least 5 behavior journal entries to see correlations
              </p>
            </div>
          )}
        </CardContent>
        <CardFooter className="flex justify-between pt-0 pb-4 px-6">
          <div className="flex items-center gap-1">
            <Badge variant="outline" className="bg-white/5 text-xs">
              {filteredBehaviorEntries.length} entries analyzed
            </Badge>
          </div>
          {behaviorCorrelations.length > 0 && (
            <Button
              variant="link"
              size="sm"
              className="text-xs p-0 h-auto text-market-teal"
            >
              View detailed analysis <ChevronRight className="h-3 w-3 ml-1" />
            </Button>
          )}
        </CardFooter>
      </Card>
    </div>
  );
};

export default BehaviorCharts;
